import api from "../api";

/**
 * Enterprise-configurable upload limits (replaces the old hardcoded rules).
 * - max_upload_file_count      : max number of files per upload  (default 20)
 * - max_upload_zip_size_mb     : ZIP/RAR/7Z/TAR/GZ archives       (default 400 MB)
 * - max_upload_document_size_mb: PDF, Word, PowerPoint, Excel     (default 100 MB)
 * - max_upload_media_size_mb   : Video & audio                    (default 50 MB)
 * - max_upload_other_size_mb   : all other supported types        (default 50 MB)
 * - allowed_upload_extensions  : comma-separated allow-list (null/empty = defaults)
 */
export const DEFAULT_UPLOAD_LIMITS = {
  max_upload_file_count: 20,
  max_upload_zip_size_mb: 400,
  max_upload_document_size_mb: 100,
  max_upload_media_size_mb: 50,
  max_upload_other_size_mb: 50,
  allowed_upload_extensions: null,
};

/**
 * Default allowed file extensions — kept in sync with the backend's legacy
 * allowedDocs / allowedMedia / allowedImages allow-lists. Used when the
 * enterprise has not configured a custom allow-list.
 *
 * NOTE: csv & xml are newly enabled types. Anything not listed here that
 * still appears in EXTENSION_GROUPS is treated as DISABLED_EXTENSIONS
 * (in-development) and rendered greyed-out in the Enterprise Settings UI.
 */
export const DEFAULT_ALLOWED_EXTENSIONS = [
  // Documents
  "pdf",
  "doc",
  "docx",
  "txt",
  "ppt",
  "pptx",
  "xls",
  "xlsx",
  "xlsm",
  "csv",
  "xml",
  "zip",
  "msg",
  // Audio
  "mp3",
  "wav",
  "aac",
  "flac",
  "m4a",
  // Video
  "mp4",
  "mov",
  "avi",
  "mkv",
  "flv",
  // Images
  "png",
  "jpg",
  "jpeg",
  "gif",
  "bmp",
  "tiff",
];

/**
 * Extensions that are NOT allowed yet (in-development / disabled). They still
 * render as greyed-out checkboxes in the Enterprise Settings UI so admins can
 * see them, but they cannot be enabled until removed from this list in code.
 *
 * To allow one later: delete it from this array and it becomes selectable
 * everywhere (frontend pickers, accept attrs, backend fileFilter).
 */
export const DISABLED_EXTENSIONS = [
  // Archives — disabled (in development)
  
  "rar",
  "7z",
  "tar",
  "gz",
  // Email
  
  // Audio — disabled (in development)
  "ogg",
  "wma",
  // Video — disabled (in development)
  "webm",
  "wmv",
  "m4v",
  "mpeg",
  "3gp",
  // Images — disabled (in development)
  "webp",
  // Other
  "fld",
];

/**
 * Extension groups shown as checkboxes in the Enterprise Settings UI.
 * `exts` are currently allowed (enabled checkboxes); `disabledExts` render
 * greyed-out until enabled from code. Custom extensions added via the input
 * box are shown separately.
 */
export const EXTENSION_GROUPS = [
  {
    label: "Documents",
    exts: ["pdf", "doc", "docx", "txt", "ppt", "pptx", "xls", "xlsx", "xlsm", "csv", "xml","msg"],
    disabledExts: ["fld"],
  },
  {
    label: "Archives (ZIP/RAR)",
    exts: ["zip"],
    disabledExts: ["rar", "7z", "tar", "gz"],
  },
  {
    label: "Audio",
    exts: ["mp3", "wav", "aac", "flac", "m4a"],
    disabledExts: ["ogg", "wma"],
  },
  {
    label: "Video",
    exts: ["mp4", "mov", "avi", "mkv", "flv"],
    disabledExts: ["webm", "wmv", "m4v", "mpeg", "3gp"],
  },
  {
    label: "Images",
    exts: ["png", "jpg", "jpeg", "gif", "bmp", "tiff"],
    disabledExts: ["webp"],
  },
];

/** Normalize a raw allowed-extension value (string, array, null) to an array. */
export function normalizeAllowedExtensions(value) {
  if (!value) return [];
  const parts = Array.isArray(value) ? value : String(value).split(/[,\s]+/);
  return [
    ...new Set(
      parts
        .map((p) => String(p).trim().toLowerCase().replace(/^\./, ""))
        .filter((p) => p && /^[a-z0-9]{1,10}$/.test(p)),
    ),
  ];
}

let cachedLimits = null;

/**
 * Fetch the current user's enterprise upload limits (cached per session).
 * Returns defaults on failure — but does NOT cache the failure, so a later
 * transient network error doesn't permanently disable enterprise limits.
 */
export async function fetchUploadLimits() {
  if (cachedLimits) return cachedLimits;

  try {
    const res = await api.get("/enterprise-document-settings/upload-limits");
    const data = res.data || {};

    cachedLimits = {
      max_upload_file_count:
        Number(data.max_upload_file_count) > 0
          ? Number(data.max_upload_file_count)
          : DEFAULT_UPLOAD_LIMITS.max_upload_file_count,
      max_upload_zip_size_mb:
        Number(data.max_upload_zip_size_mb) > 0
          ? Number(data.max_upload_zip_size_mb)
          : DEFAULT_UPLOAD_LIMITS.max_upload_zip_size_mb,
      max_upload_document_size_mb:
        Number(data.max_upload_document_size_mb) > 0
          ? Number(data.max_upload_document_size_mb)
          : DEFAULT_UPLOAD_LIMITS.max_upload_document_size_mb,
      max_upload_media_size_mb:
        Number(data.max_upload_media_size_mb) > 0
          ? Number(data.max_upload_media_size_mb)
          : DEFAULT_UPLOAD_LIMITS.max_upload_media_size_mb,
      max_upload_other_size_mb:
        Number(data.max_upload_other_size_mb) > 0
          ? Number(data.max_upload_other_size_mb)
          : DEFAULT_UPLOAD_LIMITS.max_upload_other_size_mb,
      allowed_upload_extensions:
        data.allowed_upload_extensions != null &&
        String(data.allowed_upload_extensions).trim() !== ""
          ? String(data.allowed_upload_extensions)
          : null,
    };
  } catch (err) {
    console.error("Failed to fetch upload limits, using defaults:", err);
    return { ...DEFAULT_UPLOAD_LIMITS };
  }

  return cachedLimits;
}

export function getMaxFileCount(limits = DEFAULT_UPLOAD_LIMITS) {
  return (
    Number(limits?.max_upload_file_count) ||
    DEFAULT_UPLOAD_LIMITS.max_upload_file_count
  );
}

/**
 * Effective allowed extension list — the enterprise-configured allow-list when
 * present, otherwise the default list. Accepts a limits object (from
 * fetchUploadLimits) or a raw string/array.
 *
 * DISABLED_EXTENSIONS are always stripped out, so even a stale enterprise
 * config saved before they were disabled cannot resurrect them.
 */
export function getAllowedExtensions(limitsOrRaw = DEFAULT_UPLOAD_LIMITS) {
  let normalized;
  if (typeof limitsOrRaw === "string" || Array.isArray(limitsOrRaw)) {
    normalized = normalizeAllowedExtensions(limitsOrRaw);
  } else {
    normalized = normalizeAllowedExtensions(
      limitsOrRaw?.allowed_upload_extensions,
    );
  }
  const enabled = normalized.filter((e) => !DISABLED_EXTENSIONS.includes(e));
  return enabled.length ? enabled : DEFAULT_ALLOWED_EXTENSIONS;
}

/** Whether a filename's extension is allowed by the enterprise config. */
export function isExtensionAllowed(filename = "", limitsOrRaw = DEFAULT_UPLOAD_LIMITS) {
  const ext = String(filename || "").split(".").pop()?.toLowerCase();
  return getAllowedExtensions(limitsOrRaw).includes(ext);
}

/** Build a file-input accept attribute from the allowed extensions. */
export function getAcceptAttr(limitsOrRaw = DEFAULT_UPLOAD_LIMITS) {
  return getAllowedExtensions(limitsOrRaw)
    .map((e) => `.${e}`)
    .join(",");
}

/** Upload categories that map to the enterprise-configured size limits. */
export const FILE_CATEGORIES = {
  zip: ["zip", "rar", "7z", "tar", "gz"],
  document: ["pdf", "doc", "docx", "ppt", "pptx", "xls", "xlsx"],
  media: [
    "mp3", "wav", "aac", "flac", "ogg", "m4a", "wma",
    "mp4", "mov", "avi", "mkv", "flv", "webm", "wmv", "m4v", "mpeg", "3gp",
  ],
};

/** Determine the upload category for a filename. */
export function getFileCategory(filename = "") {
  const ext = String(filename || "").split(".").pop()?.toLowerCase();
  for (const [category, exts] of Object.entries(FILE_CATEGORIES)) {
    if (exts.includes(ext)) return category;
  }
  return "other";
}

const CATEGORY_LIMIT_KEY = {
  zip: "max_upload_zip_size_mb",
  document: "max_upload_document_size_mb",
  media: "max_upload_media_size_mb",
  other: "max_upload_other_size_mb",
};

/** Size limit (MB) for a single file, based on its category. */
export function getSizeLimitMb(filename = "", limits = DEFAULT_UPLOAD_LIMITS) {
  const category = getFileCategory(filename);
  return (
    Number(limits?.[CATEGORY_LIMIT_KEY[category]]) ||
    DEFAULT_UPLOAD_LIMITS[CATEGORY_LIMIT_KEY[category]]
  );
}

/** Size limit (bytes) for a single file, based on its category. */
export function getSizeLimitBytes(filename = "", limits = DEFAULT_UPLOAD_LIMITS) {
  return getSizeLimitMb(filename, limits) * 1024 * 1024;
}
